import { useDataChainId } from '@ui/hooks/datachain';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import { Box } from 'styled-system/jsx';

import { useFromInput } from '../../hooks/useFromInput';
import { useGetPayableTokenBalance } from '../../hooks/useGetPayableTokenBalance';
import { useOpenPositionPairSpreadPercentage } from '../../hooks/useOpenPositionPairSpreadPercentage';
import { usePayTokenPriceLatest } from '../../hooks/usePayTokenPriceLatest';
import { useLeverageValue } from '../../store/useLeverage';
import { useTradingPair } from '../../store/useTradingPair';
import { CollateralInput } from '../CollateralInput';
import { useCollateralInputValidation } from './hooks/useCollateralInputValidation';
import { useEntryPriceSnapshot } from './hooks/useEntryPrice';
import { useEstOpenPositionGasFee } from './hooks/useEstOpenPositionGasFee';
import { useIsConfirmBtnDisabled } from './hooks/useIsConfirmBtnDisabled';
import { useMaxInput } from './hooks/useMaxInput';
import { useMinInput } from './hooks/useMinInput';
import { useCollateralToken } from './store/useCollateralToken';
import { getCollateralAmountErrorMessage } from './utils/validateCollateralAmount';

export const CollateralInputSection = () => {
  const { t } = useTranslation();
  const leverage = useLeverageValue((s) => s._persistValue);

  const chainId = useDataChainId();
  const input = useFromInput((s) => s.input);
  const setInput = useFromInput((s) => s.setInput);
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const setCollateralToken = useCollateralToken((s) => s.setCollateralToken);

  const payTokenPrice = usePayTokenPriceLatest(payToken, 2000);
  const tokenBalance = useGetPayableTokenBalance();

  const collateralAmountValidation = useCollateralInputValidation();
  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());

  const openPositionPairSpreadPercentage = useOpenPositionPairSpreadPercentage({
    isClosePrice: false,
  });

  const minInput = useMinInput({
    payTokenPrice,
    spreadPercentage: openPositionPairSpreadPercentage,
  });

  const maxInput = useMaxInput({
    payTokenPrice,
    leverage,
    pairInfos: currentTradePairInfo,
    spreadPercentage: openPositionPairSpreadPercentage,
  });
  const getErrorMessage = useCallback(
    (isInputToken: boolean) =>
      getCollateralAmountErrorMessage({
        t,
        error: collateralAmountValidation?.error,
        leverage,
        isInputToken,
        symbol: payToken?.displaySymbol,
        minInput,
        maxInput,
        chainId,
      }),
    [
      t,
      collateralAmountValidation?.error,
      leverage,
      payToken?.displaySymbol,
      minInput,
      maxInput,
      chainId,
    ],
  );
  const [isConfirmBtnDisabled] = useIsConfirmBtnDisabled();
  const fee = useEstOpenPositionGasFee({
    disabled: !!isConfirmBtnDisabled,
  });

  return (
    <Box flex={1}>
      <CollateralInput
        gasFee={fee}
        token={payToken}
        setToken={(token) => {
          setCollateralToken(token, chainId);
        }}
        onInput={(_input) => {
          setInput({
            ...input,
            value: _input,
          });
        }}
        value={input.value}
        tokenPrice={payTokenPrice}
        tokenBalance={tokenBalance}
        getErrorMessage={getErrorMessage}
        leverageSetterEnabled={true}
      />
    </Box>
  );
};
